import React from 'react'
import { Box } from '@chakra-ui/react'
import { SetStateFn } from '~config/constants'
import SwitchButton from '~components/shared/switch-button'
import { AuthorityLevel } from '~graphql/generated/graphql'
import FilterSelect from '~components/appointments/all/header/action-bar/filter-select'
import {
    Button,
    PopoverTrigger,
    Popover,
    Tooltip,
    useBreakpointValue,
    PopoverContent,
    PopoverArrow,
    Icon,
    Switch,
    Spacer,
} from '@chakra-ui/react'
import { useCliniciansContext } from './provider'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { BiFilter } from 'react-icons/bi'
import SharedLabel from '~components/shared/form/shared-label'
import MenuMultiselect from '~components/shared/form/menu-multiselect'
import SharedSectionHeader from '~components/shared/texts/shared-section-header'
import ClearButton from '~components/shared/clear-button'
import InfoTooltip from '~components/shared/info-tooltip'

export function AuthorityLevelSelect({
    authorityLevel,
    setAuthorityLevel,
}: {
    authorityLevel: AuthorityLevel[]
    setAuthorityLevel: SetStateFn<AuthorityLevel[]>
}) {
    return (
        <FilterSelect
            values={authorityLevel}
            setValues={values => setAuthorityLevel(values as AuthorityLevel[])}
            options={Object.values(AuthorityLevel).map(level => ({
                value: level,
                label: level,
            }))}
            label="Authority Level"
            countOnlyDisplay
        />
    )
}

export function ShowInactiveSwitch({
    showInactive,
    setShowInactive,
    textOverride,
}: {
    showInactive: boolean
    setShowInactive: SetStateFn<boolean>
    textOverride?: string
}) {
    return (
        <SwitchButton
            isChecked={showInactive}
            onClick={() => setShowInactive(prev => !prev)}
        >
            {textOverride || 'Show Inactive?'}
        </SwitchButton>
    )
}

export function ShowNonSystemSwitch({
    showNonSystem,
    setShowNonSystem,
}: {
    showNonSystem: boolean
    setShowNonSystem: SetStateFn<boolean>
}) {
    const text = useBreakpointValue({
        base: 'Non-System?',
        md: 'Include Non-System Clients?',
    })

    return (
        <SwitchButton
            isChecked={showNonSystem}
            onClick={() => setShowNonSystem(prev => !prev)}
        >
            {text}
        </SwitchButton>
    )
}

export function ShowPmsInactiveSwitch({
    showPmsInactive,
    setShowPmsInactive,
}: {
    showPmsInactive: boolean
    setShowPmsInactive: SetStateFn<boolean>
}) {
    return (
        <Tooltip
            label="Show clinicians that are inactive in your Practice Management System (PMS)."
            hasArrow
            openDelay={400}
        >
            <Box>
                <SwitchButton
                    isChecked={showPmsInactive}
                    onClick={() => setShowPmsInactive(prev => !prev)}
                >
                    Show PMS Inactive?
                </SwitchButton>
            </Box>
        </Tooltip>
    )
}

export function ShowContributesToFillRateSwitch({
    showContributesToFillRate,
    setShowContributesToFillRate,
}: {
    showContributesToFillRate: boolean
    setShowContributesToFillRate: SetStateFn<boolean>
}) {
    return (
        <Tooltip
            label="Only show clinicians that contribute to fill rate calculations in analytics."
            hasArrow
            openDelay={400}
        >
            <Box>
                <SwitchButton
                    isChecked={showContributesToFillRate}
                    onClick={() => setShowContributesToFillRate(prev => !prev)}
                >
                    Fill Rate Contributors?
                </SwitchButton>
            </Box>
        </Tooltip>
    )
}

export function CliniciansAdvancedFilterMenu() {
    const {
        showOtherLocationClinicians,
        setShowOtherLocationClinicians,
        authorityLevel,
        setAuthorityLevel,
    } = useCliniciansContext()

    const activeFilters = [showOtherLocationClinicians].filter(Boolean).length

    const isActive = activeFilters > 0

    function resetState() {
        setShowOtherLocationClinicians(false)
        setAuthorityLevel([])
    }

    return (
        <Popover>
            <PopoverTrigger>
                <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<Icon as={BiFilter} fontSize="lg" />}
                    colorScheme={isActive ? 'brand' : 'gray'}
                >
                    Advanced{isActive && ` (${activeFilters})`}
                </Button>
            </PopoverTrigger>
            <PopoverContent p={4} w="450px">
                <PopoverArrow />
                <SharedStack>
                    <SharedStack spaced align="center" row mb={4}>
                        <SharedStack fit row>
                            <Icon
                                as={BiFilter}
                                fontSize="xl"
                                color="navy.800"
                            />
                            <SharedSectionHeader>Filters</SharedSectionHeader>
                        </SharedStack>
                        {isActive && (
                            <ClearButton onClick={resetState} boxShadow="xs" />
                        )}
                    </SharedStack>
                    <SharedStack row spaced>
                        <SharedLabel>Authority level(s)</SharedLabel>
                        <MenuMultiselect
                            size="xs"
                            variant="solid"
                            values={authorityLevel}
                            setValues={values => {
                                setAuthorityLevel(values as AuthorityLevel[])
                            }}
                            options={Object.values(AuthorityLevel).map(
                                level => ({
                                    value: level,
                                    label: level,
                                }),
                            )}
                            textModification={level =>
                                level === AuthorityLevel.NonTechnicalStaff
                                    ? 'Non-Technical Staff'
                                    : level
                            }
                        />
                    </SharedStack>
                    <SharedStack row spaced>
                        <SharedLabel>
                            Show other location clinicians?{' '}
                        </SharedLabel>
                        <InfoTooltip tooltip="If true, will show clinicians mapped to resources in other locations from your PIMS." />
                        <Spacer />
                        <Switch
                            colorScheme="brand"
                            isChecked={showOtherLocationClinicians}
                            onChange={() =>
                                setShowOtherLocationClinicians(
                                    !showOtherLocationClinicians,
                                )
                            }
                        />
                    </SharedStack>
                </SharedStack>
            </PopoverContent>
        </Popover>
    )
}
